import { useState } from 'react';

import { ArrowLeft } from 'lucide-react';

import { useModal } from '@contexts/modal-context/ModalContext';
import type {
  ArtifactContent,
  ArtifactsResponse,
  CreateAgentResponse,
} from '@features/integrations/api/create-agent.types';
import { GenericModal } from '@shared/components/ui/custom/GenericModal';
import { MODAL_KEYS } from '@shared/components/ui/custom/global-modal-manager/global-manager.types';

import type { AgentType, Step } from './create-agent.utils';
import { AGENT_TYPE, STEP } from './create-agent.utils';
import { getCreateAgentHeaderConfig } from './sub-components/general/create-agent-header.utils';
import { DownloadAgentPackage } from './sub-components/general/DownloadAgentPackage';
import type { AgentDetailsFormValues } from './sub-components/general/generic-agent-details.schema';
import { SelectAgentType } from './sub-components/general/SelectAgentType';
import { OpAgentDetails } from './sub-components/op-agent/OpAgentDetails.form';
import { SapArtifactUpload } from './sub-components/standalone-agent/SapArtifactUpload';
import { StandaloneAgentDetails } from './sub-components/standalone-agent/StandaloneAgentDetails.form';

export const CreateAgentModalController = () => {
  const { closeModal } = useModal();
  const [currentStep, setCurrentStep] = useState<Step>(STEP.AGENT_TYPE_SELECTION);
  const [downloadLink, setDownloadLink] = useState<string>('');
  const [stagedFile, setStagedFile] = useState<File[]>([]);
  const [alreadyUploadedArtifacts, setAlreadyUploadedArtifacts] = useState<ArtifactContent[]>([]);
  const [agentFormDetails, setAgentFormDetails] = useState<AgentDetailsFormValues | null>(null);
  const [agentId, setAgentId] = useState<string>('');

  const onAgentTypeSelected = (type: AgentType) => {
    if (type === AGENT_TYPE.STANDALONE_AGENT) {
      setCurrentStep(STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT);
    } else if (type === AGENT_TYPE.OP_AGENT) {
      setCurrentStep(STEP.OP_AGENT_ENTER_DETAILS);
    }
  };

  const handleBackNavigation = () => {
    setCurrentStep(STEP.AGENT_TYPE_SELECTION);
  };

  const headerProps = getCreateAgentHeaderConfig({
    currentStep,
    stagedFile,
    alreadyUploadedArtifacts,
    agentFormDetails,
    renderBackButton: () => (
      <button
        type="button"
        onClick={handleBackNavigation}
        className="p-1 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        aria-label="Navigate back"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
    ),
  });

  const onGenerateSuccess = (response: CreateAgentResponse, payload: AgentDetailsFormValues, nextStep: Step) => {
    setAgentId(response?.id ?? '');
    setAgentFormDetails(payload);
    setDownloadLink(response?.bundleLink ?? '');
    setCurrentStep(nextStep);
  };

  const onProceedWithExistingArtifacts = (artifactsData?: ArtifactsResponse) => {
    setAlreadyUploadedArtifacts(artifactsData?.content ?? []);
    setStagedFile([]);
    setCurrentStep(STEP.STANDALONE_AGENT_ENTER_DETAILS);
  };

  const onUploadArtifactSuccess = (rawFile: File) => {
    setAlreadyUploadedArtifacts([]);
    setStagedFile([rawFile]);
    setCurrentStep(STEP.STANDALONE_AGENT_ENTER_DETAILS);
  };

  const onClose = () => {
    closeModal(MODAL_KEYS.CREATE_AGENT);
  };

  const downloadAgentPackageProps = {
    agentId,
    downloadLink,
    setDownloadLink,
    onClose,
  };

  const CreateAgentModalStep = {
    // Agent Type Selection Step (Standalone Agent or OP Integration selection)
    [STEP.AGENT_TYPE_SELECTION]: <SelectAgentType onAgentTypeSelected={onAgentTypeSelected} onClose={onClose} />,

    // Standalone Agent Steps
    [STEP.STANDALONE_AGENT_UPLOAD_ARTIFACT]: (
      <SapArtifactUpload
        onProceedWithExistingArtifacts={onProceedWithExistingArtifacts}
        onUploadArtifactSuccess={onUploadArtifactSuccess}
        stagedFile={stagedFile}
        onClose={onClose}
      />
    ),
    [STEP.STANDALONE_AGENT_ENTER_DETAILS]: (
      <StandaloneAgentDetails onGenerateSuccess={onGenerateSuccess} onClose={onClose} />
    ),
    [STEP.STANDALONE_AGENT_GENERATE_BUNDLE]: <DownloadAgentPackage {...downloadAgentPackageProps} />,

    // OP Integration Steps
    [STEP.OP_AGENT_ENTER_DETAILS]: <OpAgentDetails onGenerateSuccess={onGenerateSuccess} onClose={onClose} />,
    [STEP.OP_AGENT_GENERATE_BUNDLE]: <DownloadAgentPackage {...downloadAgentPackageProps} />,
  }[currentStep];

  return (
    <GenericModal
      open
      title={headerProps.title}
      description={headerProps.description}
      onOpenChange={(open) => !open && onClose()}
    >
      {CreateAgentModalStep}
    </GenericModal>
  );
};
